import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';

import UnifiedIcon from '@/components/UnifiedIcon';
import Portal from '@/designSystem/overlays/Portal';

const BUTTON_SIZES = {
  sm: 'h-8 w-8 p-1',
  md: 'h-10 w-10 p-2',
  lg: 'h-12 w-12 p-3',
} as const;

const ICON_SIZES = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
} as const;

export type KebabMenuItem = {
  id: string;
  label: string;
  icon?: string;
  iconLibrary?: 'phosphor' | 'tabler';
  onClick?: (data?: unknown, event?: ReactMouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  className?: string;
  data?: unknown;
  ariaLabel?: string;
  style?: CSSProperties;
  keepMenuOpen?: boolean;
  type?: 'item' | 'section';
  items?: KebabMenuItem[];
  /** Stable selector for the menu entry (E2E contract). */
  testId?: string;
};

export type KebabMenuProps = {
  items?: KebabMenuItem[];
  triggerClassName?: string;
  menuClassName?: string;
  buttonSize?: keyof typeof BUTTON_SIZES;
  disabled?: boolean;
  ariaLabel?: string;
  onOpen?: () => void;
  onClose?: () => void;
  alignment?: 'left' | 'right';
  variant?: 'plain' | 'outlined';
  triggerIcon?: string;
  triggerIconLibrary?: 'phosphor' | 'tabler';
  openBackgroundColor?: string;
  getItemStyle?: (data?: unknown, item?: KebabMenuItem) => CSSProperties;
  toggleActiveColor?: string;
  title?: string;
  /** Stable selector for the three-dots trigger (E2E contract). */
  testId?: string;
};

type MenuCoords = {
  top: number;
  left?: number;
  right?: number;
};

const MENU_GAP_PX = 8;
const VIEWPORT_PADDING_PX = 8;
const MENU_HEIGHT_ESTIMATE_PX = 120;

/**
 * @param {HTMLButtonElement} buttonEl
 * @param {HTMLDivElement | null} menuEl
 * @param {'left' | 'right'} alignment
 * @returns {MenuCoords}
 */
function computeMenuCoords(
  buttonEl: HTMLButtonElement,
  menuEl: HTMLDivElement | null,
  alignment: 'left' | 'right'
): MenuCoords {
  const rect = buttonEl.getBoundingClientRect();
  const menuHeight = menuEl?.offsetHeight ?? MENU_HEIGHT_ESTIMATE_PX;

  let top = rect.bottom + MENU_GAP_PX;
  if (top + menuHeight > window.innerHeight - VIEWPORT_PADDING_PX) {
    top = Math.max(VIEWPORT_PADDING_PX, rect.top - menuHeight - MENU_GAP_PX);
  }

  if (alignment === 'right') {
    return { top, right: window.innerWidth - rect.right };
  }

  return { top, left: rect.left };
}

/**
 * Reusable kebab menu: three-dots trigger and dropdown actions.
 * Dropdown renders in a portal so it is not clipped by overflow ancestors.
 */
export default function KebabMenu({
  items = [],
  triggerClassName = '',
  menuClassName = '',
  buttonSize = 'md',
  disabled = false,
  ariaLabel = 'More options',
  onOpen,
  onClose,
  alignment = 'right',
  variant = 'plain',
  triggerIcon = 'DotsThreeOutlineVertical',
  triggerIconLibrary = 'phosphor',
  openBackgroundColor,
  getItemStyle,
  toggleActiveColor = '#3b82f6',
  title,
  testId,
}: KebabMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [menuCoords, setMenuCoords] = useState<MenuCoords | null>(null);
  const menuPanelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [menuId] = useState(() => `kebab-menu-${Date.now()}`);

  const toggleMenu = useCallback(
    (e?: ReactMouseEvent<HTMLButtonElement>) => {
      e?.stopPropagation();
      const newIsOpen = !isOpen;
      setIsOpen(newIsOpen);

      if (newIsOpen) {
        onOpen?.();
      } else {
        onClose?.();
      }
    },
    [isOpen, onOpen, onClose]
  );

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    onClose?.();
  }, [onClose]);

  const handleItemClick = useCallback(
    (item: KebabMenuItem, e: ReactMouseEvent<HTMLButtonElement>) => {
      e?.stopPropagation();

      if (item.disabled) return;

      const data = item.data as { isActive?: boolean } | undefined;
      if (!item.keepMenuOpen && data?.isActive === undefined) {
        closeMenu();
      }

      item.onClick?.(item.data, e);
    },
    [closeMenu]
  );

  const renderMenuItem = (item: KebabMenuItem) => {
    const dynamicStyle = getItemStyle ? getItemStyle(item.data, item) : {};
    const itemStyle: CSSProperties = { ...item.style, ...dynamicStyle };
    const data = item.data as { isActive?: boolean } | undefined;

    return (
      <button
        key={item.id}
        type="button"
        role="menuitem"
        disabled={item.disabled}
        style={itemStyle}
        className={`
          w-full text-left px-4 py-2 text-sm flex items-center gap-3 rounded-md
          text-gray-900 dark:text-gray-100
          hover:bg-gray-100 focus:bg-gray-100
          dark:hover:bg-gray-700 dark:focus:bg-gray-700
          disabled:opacity-50 disabled:cursor-not-allowed
          transition-all duration-150
          ${item.className || ''}
        `.trim()}
        onClick={(e) => handleItemClick(item, e)}
        aria-label={item.ariaLabel || item.label}
        data-testid={item.testId}
      >
        {item.icon && (
          <UnifiedIcon
            name={item.icon}
            library={item.iconLibrary || 'phosphor'}
            className="w-4 h-4 shrink-0 text-gray-500 dark:text-gray-400"
          />
        )}
        <span className="truncate flex-1">{item.label}</span>
        {data?.isActive !== undefined && (
          <div className="flex items-center ml-2">
            <div
              className="w-8 h-4 rounded-full transition-colors relative"
              style={{
                backgroundColor: data.isActive ? toggleActiveColor : '#d1d5db',
              }}
            >
              <div
                className={`w-3 h-3 bg-white rounded-full transition-transform absolute top-0.5 ${
                  data.isActive ? 'translate-x-4' : 'translate-x-0.5'
                }`}
              />
            </div>
          </div>
        )}
      </button>
    );
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeMenu();
        buttonRef.current?.focus();
      }
    },
    [closeMenu]
  );

  useLayoutEffect(() => {
    if (!isOpen || !buttonRef.current) {
      setMenuCoords(null);
      return;
    }

    const updateCoords = () => {
      if (!buttonRef.current) {
        return;
      }

      setMenuCoords(computeMenuCoords(buttonRef.current, menuPanelRef.current, alignment));
    };

    updateCoords();
    const frame = requestAnimationFrame(updateCoords);

    return () => cancelAnimationFrame(frame);
  }, [isOpen, alignment, items, title]);

  useEffect(() => {
    const handleClickOutside = (e: globalThis.MouseEvent) => {
      const target = e.target as Node;
      if (
        isOpen &&
        buttonRef.current &&
        !buttonRef.current.contains(target) &&
        (!menuPanelRef.current || !menuPanelRef.current.contains(target))
      ) {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, closeMenu]);

  if (items.length === 0) {
    return null;
  }

  const baseButtonClasses = `
    ${BUTTON_SIZES[buttonSize]}
    flex justify-center items-center
    transition-colors duration-150
    focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500
    disabled:opacity-50 disabled:cursor-not-allowed
  `.trim();

  const variantClasses: Record<NonNullable<KebabMenuProps['variant']>, string> = {
    plain: `
      rounded-lg
      ${
        isOpen && openBackgroundColor
          ? ''
          : 'hover:bg-gray-100 focus:bg-gray-100 dark:hover:bg-gray-700 dark:focus:bg-gray-700'
      }
    `.trim(),
    outlined: `
      rounded-kcMicro
      ${
        isOpen && openBackgroundColor
          ? 'border border-gray-200 dark:border-gray-600 shadow-sm'
          : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 shadow-sm hover:bg-gray-50 focus:bg-gray-50 dark:hover:bg-gray-700 dark:focus:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-500'
      }
    `.trim(),
  };

  const buttonClasses = `
    ${baseButtonClasses}
    ${variantClasses[variant]}
    ${triggerClassName}
  `.trim();

  const buttonStyle: CSSProperties | undefined =
    isOpen && openBackgroundColor ? { backgroundColor: openBackgroundColor } : undefined;

  const menuClasses = `
    z-30 rounded-kcInner
    bg-white dark:bg-gray-800 ${title ? 'py-0' : 'py-1'}
    shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-600
    focus:outline-none
    min-w-[160px]
    ${menuClassName}
  `.trim();

  const iconSize = ICON_SIZES[buttonSize];

  const menuPanel =
    isOpen && menuCoords ? (
      <div
        id={menuId}
        ref={menuPanelRef}
        className={menuClasses}
        style={{
          position: 'fixed',
          top: menuCoords.top,
          ...(menuCoords.right != null
            ? { right: menuCoords.right }
            : { left: menuCoords.left ?? 0 }),
          zIndex: 30,
        }}
        role="menu"
        aria-orientation="vertical"
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        {title && (
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-600">
            <h3
              className="text-base font-semibold text-gray-900 dark:text-gray-100 text-left"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            >
              {title}
            </h3>
          </div>
        )}

        <div className={title ? 'py-1' : ''}>
          {items.map((item, index) => {
            if (item.type === 'section') {
              return (
                <div key={item.id}>
                  {index > 0 && (
                    <div className="border-t border-gray-200 dark:border-gray-600 my-1" />
                  )}
                  {item.label?.trim() ? (
                    <div className="px-4 py-2">
                      <h4
                        className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider text-left"
                        style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                      >
                        {item.label}
                      </h4>
                    </div>
                  ) : null}
                  {item.items?.map((subItem) => renderMenuItem(subItem))}
                </div>
              );
            }

            return renderMenuItem(item);
          })}
        </div>
      </div>
    ) : null;

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        className={buttonClasses}
        style={buttonStyle}
        onClick={toggleMenu}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={isOpen ? menuId : undefined}
        data-testid={testId}
      >
        {isOpen ? (
          <UnifiedIcon
            name="X"
            library="phosphor"
            className={`${iconSize} ${openBackgroundColor ? 'text-white' : 'text-gray-600'} dark:text-gray-300`}
          />
        ) : (
          <UnifiedIcon
            name={triggerIcon}
            library={triggerIconLibrary}
            className={`${iconSize} text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200`}
          />
        )}
      </button>

      {menuPanel ? <Portal>{menuPanel}</Portal> : null}
    </div>
  );
}

export { KebabMenu };
